import { useCallback } from 'react';

import type { LocalCartAddon, Ticket } from 'restful/tickets';
import { isPaidAddon } from 'restful/tickets/addon.utils';
import { addToCart, removeFromCart } from 'services/avo';
import { useTrackEvent } from 'services/datalayer/hooks';

import { useTicketInfoLookup } from 'components/@tickets/hooks';

import { useEventBundle } from './useEventBundle';
import { mapTicketToDataLayer } from './utils';

type TrackableTicket = Pick<Ticket, 'id' | 'name'>;

const getAddonQuantity = (
  addons: Array<LocalCartAddon> | undefined,
  catalogueItemId: string,
) =>
  addons?.find((addon) => addon.catalogueItemId === catalogueItemId)
    ?.quantity ?? 0;

// Whenever a quantity of a ticket is changed in the cart
// This can also be that the complete line item is removed
export const useTrackQuantityChange = () => {
  const eventBundle = useEventBundle();
  const lookup = useTicketInfoLookup();

  const trackAdd = useTrackEvent({
    trackFn: addToCart,
    variables: eventBundle,
  });

  const trackRemove = useTrackEvent({
    trackFn: removeFromCart,
    variables: eventBundle,
  });

  const mapQuantityItems = useCallback(
    (ticket: TrackableTicket, quantity: number) => {
      const items = [];
      for (let i = 0; i < quantity; i++) {
        items.push(
          mapTicketToDataLayer(ticket, eventBundle, lookup(ticket.id)),
        );
      }
      return items;
    },
    [eventBundle, lookup],
  );

  const handleTrackAdd = useCallback(
    (ticket: TrackableTicket, quantity = 1) => {
      if (quantity <= 0) {
        return;
      }

      const items = mapQuantityItems(ticket, quantity);
      trackAdd({
        items,
        currency: 'SAR',
        value: items.reduce((acc, item) => acc + item.price, 0),
      });
    },
    [mapQuantityItems, trackAdd],
  );

  const handleTrackRemove = useCallback(
    (ticket: TrackableTicket, quantity = 1) => {
      if (quantity <= 0) {
        return;
      }

      const items = mapQuantityItems(ticket, quantity);
      trackRemove({
        items,
        currency: 'SAR',
        value: items.reduce((acc, item) => acc + item.price, 0),
      });
    },
    [mapQuantityItems, trackRemove],
  );

  const handleTrackPaidAddonDiff = useCallback(
    (
      previous: Array<LocalCartAddon> | undefined,
      next: Array<LocalCartAddon> | undefined,
    ) => {
      const catalogueItemIds = new Set([
        ...(previous?.map((addon) => addon.catalogueItemId) ?? []),
        ...(next?.map((addon) => addon.catalogueItemId) ?? []),
      ]);

      catalogueItemIds.forEach((catalogueItemId) => {
        const previousAddon = previous?.find(
          (addon) => addon.catalogueItemId === catalogueItemId,
        );
        const nextAddon = next?.find(
          (addon) => addon.catalogueItemId === catalogueItemId,
        );
        const addon = nextAddon ?? previousAddon;

        if (!addon || !isPaidAddon(addon.ticketType, addon.addonType)) {
          return;
        }

        const delta =
          getAddonQuantity(next, catalogueItemId) -
          getAddonQuantity(previous, catalogueItemId);

        if (delta > 0) {
          handleTrackAdd({ id: catalogueItemId, name: addon.name }, delta);
          return;
        }

        if (delta < 0) {
          handleTrackRemove({ id: catalogueItemId, name: addon.name }, -delta);
        }
      });
    },
    [handleTrackAdd, handleTrackRemove],
  );

  return {
    trackAdd: handleTrackAdd,
    trackRemove: handleTrackRemove,
    trackPaidAddonDiff: handleTrackPaidAddonDiff,
  };
};
